// src/components/Payments.js - Updated with centered search functionality
import React, { useState, useEffect } from 'react';
import { apiService } from '../services/apiService';
import { useAuth } from '../context/AuthContext';

const Payments = () => {
  const [payments, setPayments] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('list'); // 'list', 'create', 'history'
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [searchTerm, setSearchTerm] = useState(''); // Add search state
  
  // Create payment form state
  const [newPayment, setNewPayment] = useState({
    orderId: '',
    amount: 0,
    paymentMethod: 'CREDIT_CARD'
  });
  const [creating, setCreating] = useState(false);

  // Filter state
  const [statusFilter, setStatusFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');

  const { user } = useAuth();

  useEffect(() => {
    loadPaymentData();
  }, []);

  const loadPaymentData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load payments - your API returns paginated response with content array
      const paymentsResponse = await apiService.getAllPayments();
      console.log('Payments response received:', paymentsResponse);
      
      // Extract payments from paginated response
      let paymentsArray = [];
      if (paymentsResponse && paymentsResponse.content) {
        paymentsArray = paymentsResponse.content;
      } else if (Array.isArray(paymentsResponse)) {
        paymentsArray = paymentsResponse;
      }
      
      setPayments(paymentsArray);
      
      // Try to load orders
      try {
        const ordersData = await apiService.getAllOrders();
        setOrders(Array.isArray(ordersData) ? ordersData : []);
      } catch (orderErr) {
        console.warn('Could not load orders:', orderErr);
        setOrders([]);
      }
      
    } catch (err) {
      console.error('Failed to load payments:', err);
      setError(`Failed to load payment data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePayment = async (e) => {
    e.preventDefault();
    if (!newPayment.orderId || newPayment.amount <= 0) {
      setError('Please select an order and enter a valid amount');
      return;
    }

    try {
      setCreating(true);
      setError(null);
      
      const paymentData = {
        orderId: parseInt(newPayment.orderId),
        amount: newPayment.amount,
        paymentMethod: newPayment.paymentMethod
      };

      const createdPayment = await apiService.createPayment(paymentData);
      setPayments([createdPayment, ...payments]);
      setNewPayment({ orderId: '', amount: 0, paymentMethod: 'CREDIT_CARD' });
      setActiveTab('list');
      
      alert('Payment created successfully!');
    } catch (err) {
      setError(`Failed to create payment: ${err.message}`);
    } finally {
      setCreating(false);
    }
  };

  const handleOrderChange = (orderId) => {
    const selectedOrder = orders.find(o => o.id === parseInt(orderId));
    if (selectedOrder) {
      const orderTotal = selectedOrder.totalAmount || (selectedOrder.quantity * selectedOrder.price) || selectedOrder.price || 0;
      setNewPayment({
        ...newPayment,
        orderId,
        amount: orderTotal
      });
    }
  };

  const viewPaymentDetails = async (paymentId) => {
    try {
      const paymentDetails = await apiService.getPaymentById(paymentId);
      setSelectedPayment(paymentDetails);
    } catch (err) {
      // If specific payment API fails, use the payment from current list
      const payment = payments.find(p => p.id === paymentId);
      if (payment) {
        setSelectedPayment(payment);
      } else {
        setError(`Failed to load payment details: ${err.message}`);
      }
    }
  };

  const processPayment = async (paymentId) => {
    try {
      await apiService.processPayment(paymentId);
      await loadPaymentData(); // Reload to see updated status
      alert('Payment processed successfully!');
    } catch (err) {
      setError(`Failed to process payment: ${err.message}`);
    }
  };

  const formatDate = (dateArray) => {
    if (!dateArray || !Array.isArray(dateArray)) return 'N/A';
    const [year, month, day, hour = 0, minute = 0, second = 0] = dateArray;
    return new Date(year, month - 1, day, hour, minute, second).toLocaleString();
  };

  const formatDateShort = (dateArray) => {
    if (!dateArray || !Array.isArray(dateArray)) return 'N/A';
    const [year, month, day] = dateArray;
    return `${month}/${day}/${year}`;
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'PENDING': return '#ffc107';
      case 'PROCESSING': return '#17a2b8';
      case 'SUCCESS': return '#28a745';
      case 'COMPLETED': return '#28a745';
      case 'FAILED': return '#dc3545';
      case 'REFUNDED': return '#6c757d';
      default: return '#6c757d';
    }
  };

  const getMethodIcon = (method) => {
    switch (method?.toUpperCase()) {
      case 'CREDIT_CARD': return '💳';
      case 'DEBIT_CARD': return '💳';
      case 'PAYPAL': return '🅿️';
      case 'BANK_TRANSFER': return '🏦';
      case 'UPI': return '📱';
      default: return '💰';
    }
  };

  const getMethodDisplay = (method) => {
    return method?.replace('_', ' ') || 'N/A';
  };

  // Add search filtering function
  const getFilteredAndSearchedPayments = () => {
    let filtered = payments.filter(payment => {
      const statusMatch = statusFilter === 'all' || payment.status?.toLowerCase() === statusFilter.toLowerCase();
      const methodMatch = methodFilter === 'all' || payment.paymentMethod?.toLowerCase() === methodFilter.toLowerCase();
      return statusMatch && methodMatch;
    });

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(payment => {
        return (
          payment.id.toString().includes(searchTerm) ||
          payment.orderId?.toString().includes(searchTerm) ||
          payment.status?.toLowerCase().includes(searchLower) ||
          payment.paymentMethod?.toLowerCase().includes(searchLower) ||
          payment.transactionId?.toLowerCase().includes(searchLower) ||
          payment.customerId?.toLowerCase().includes(searchLower) ||
          payment.amount?.toString().includes(searchTerm)
        );
      });
    }

    return filtered;
  };

  // Filter payments based on selected filters and search
  const filteredPayments = getFilteredAndSearchedPayments();

  // Get payment statistics
  const getPaymentStats = () => {
    const totalAmount = payments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
    const successPayments = payments.filter(p => p.status?.toLowerCase() === 'success');
    const pendingPayments = payments.filter(p => p.status?.toLowerCase() === 'pending');
    const failedPayments = payments.filter(p => p.status?.toLowerCase() === 'failed');

    return {
      total: payments.length,
      totalAmount,
      completed: successPayments.length,
      pending: pendingPayments.length,
      failed: failedPayments.length,
      completedAmount: successPayments.reduce((sum, p) => sum + (p.amount || 0), 0)
    };
  };

  const stats = getPaymentStats();

  // Get orders that don't have payments yet
  const getUnpaidOrders = () => {
    const paidOrderIds = payments.map(p => p.orderId);
    return orders.filter(order => !paidOrderIds.includes(order.id));
  };

  if (loading) {
    return <div className="loading">Loading payments...</div>;
  }

  return (
    <div className="payments-container">
      <div className="payments-header">
        <h1></h1>
        <div className="tab-buttons">
          <button 
            className={`tab-btn ${activeTab === 'list' ? 'active' : ''}`}
            onClick={() => setActiveTab('list')}
          >
            Payment List
          </button>
          <button 
            className={`tab-btn ${activeTab === 'create' ? 'active' : ''}`}
            onClick={() => setActiveTab('create')}
          >
            Create Payment
          </button>
          <button 
            className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            Payment History
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)} className="close-btn">×</button>
        </div>
      )}

      {/* Payment Statistics - Using inventory summary card style */}
      <div className="inventory-summary">
        <div className="summary-cards">
          <div className="summary-card">
            <h3>Total Payments</h3>
            <div className="summary-number">{stats.total}</div>
          </div>
          <div className="summary-card">
            <h3>Total Amount</h3>
            <div className="summary-number">${stats.totalAmount.toFixed(2)}</div>
          </div>
          <div className="summary-card">
            <h3>Completed</h3>
            <div className="summary-number" style={{ color: '#28a745' }}>{stats.completed}</div>
            <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>
              ${stats.completedAmount.toFixed(2)}
            </div>
          </div>
          <div className="summary-card">
            <h3>Pending</h3>
            <div className="summary-number warning">{stats.pending}</div>
          </div>
          <div className="summary-card">
            <h3>Failed</h3>
            <div className="summary-number" style={{ color: '#dc3545' }}>{stats.failed}</div>
          </div>
        </div>
      </div>

      {activeTab === 'list' && (
        <div className="payments-list">
          {/* Updated header structure for proper centering */}
          <div className="orders-header-section">
            <div className="orders-title-section">
              <h3>All Payments ({filteredPayments.length})</h3>
            </div>
            
            <div className="search-container-centered">
              <input
                type="text"
                placeholder="Search payments by ID, status, method, amount..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input-centered"
              />
            </div>
            
            <div className="pagination-info-section">
              <div className="header-actions">
                <div className="filter-group">
                  <label>Status:</label>
                  <select 
                    value={statusFilter} 
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="filter-select"
                  >
                    <option value="all">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="success">Success</option>
                    <option value="failed">Failed</option>
                    <option value="refunded">Refunded</option>
                  </select>
                </div>
                <div className="filter-group">
                  <label>Method:</label>
                  <select 
                    value={methodFilter} 
                    onChange={(e) => setMethodFilter(e.target.value)}
                    className="filter-select"
                  >
                    <option value="all">All Methods</option>
                    <option value="credit_card">Credit Card</option>
                    <option value="debit_card">Debit Card</option>
                    <option value="paypal">PayPal</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="upi">UPI</option>
                  </select>
                </div>
                <button onClick={loadPaymentData} className="refresh-btn">Refresh</button>
              </div>
            </div>
          </div>

          {filteredPayments.length === 0 ? (
            <div className="no-payments">
              <p>No payments found matching your criteria.</p>
              <button 
                onClick={() => setActiveTab('create')} 
                className="create-first-payment-btn"
              >
                Create First Payment
              </button>
            </div>
          ) : (
            <div className="orders-table-modern">
              <table>
                <thead>
                  <tr>
                    <th>Payment ID</th>
                    <th>Order ID</th>
                    <th>Amount</th>
                    <th>Method</th>
                    <th>Customer</th>
                    <th>Created</th>
                    <th>Transaction ID</th>
                    <th>Status</th>
                    <th>Processed</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPayments.map(payment => (
                    <tr key={payment.id}>
                      <td className="order-id-cell">#{payment.id}</td>
                      <td className="order-id-cell">#{payment.orderId}</td>
                      <td className="amount-cell-modern">${payment.amount?.toFixed(2)}</td>
                      <td className="category-cell">{getMethodDisplay(payment.paymentMethod)}</td>
                      <td className="customer-id-cell">
                        {payment.customerId ? `${payment.customerId.substring(0, 8)}...` : 'N/A'}
                      </td>
                      <td className="date-cell">{formatDateShort(payment.createdAt)}</td>
                      <td className="transaction-id-cell">
                        {payment.transactionId ? `${payment.transactionId.substring(0, 12)}...` : 'N/A'}
                      </td>
                      <td>
                        <span 
                          className="status-badge-modern"
                          style={{ 
                            color: getStatusColor(payment.status), 
                            backgroundColor: "white", 
                            borderColor: getStatusColor(payment.status), 
                            border: "2px solid"
                          }}
                        >
                          {payment.status || 'PENDING'}
                        </span>
                      </td>
                      <td className="date-cell">{formatDateShort(payment.processedAt)}</td>
                      <td>
                        <button 
                          className="action-btn-modern" 
                          onClick={() => viewPaymentDetails(payment.id)}
                          title="View Payment Details"
                        >
                          V
                        </button>
                        {payment.status?.toLowerCase() === 'pending' && (
                          <button 
                            className="action-btn-modern restock-action" 
                            onClick={() => processPayment(payment.id)}
                            title="Process Payment"
                            style={{ marginLeft: '5px' }}
                          >
                            ⚡
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'create' && (
        <div className="create-payment">
          <h2>Create New Payment</h2>
          
          {getUnpaidOrders().length === 0 ? (
            <div className="no-unpaid-orders">
              <p>All orders have payments. Create an order first to make a payment.</p>
              <button 
                onClick={() => window.location.href = '/orders'}
                className="goto-orders-btn"
              >
                Go to Orders
              </button>
            </div>
          ) : (
            <form onSubmit={handleCreatePayment} className="payment-form">
              <div className="form-group">
                <label htmlFor="orderId">Select Order:</label>
                <select
                  id="orderId"
                  value={newPayment.orderId}
                  onChange={(e) => handleOrderChange(e.target.value)}
                  required
                  className="form-control"
                >
                  <option value="">Choose an order...</option>
                  {getUnpaidOrders().map(order => (
                    <option key={order.id} value={order.id}>
                      Order #{order.id} - ${(order.totalAmount || order.price || 0).toFixed(2)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="amount">Payment Amount:</label>
                <input
                  type="number"
                  id="amount"
                  step="0.01"
                  min="0.01"
                  value={newPayment.amount}
                  onChange={(e) => setNewPayment({...newPayment, amount: parseFloat(e.target.value)})}
                  required
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label htmlFor="paymentMethod">Payment Method:</label>
                <select
                  id="paymentMethod"
                  value={newPayment.paymentMethod}
                  onChange={(e) => setNewPayment({...newPayment, paymentMethod: e.target.value})}
                  required
                  className="form-control"
                >
                  <option value="CREDIT_CARD">Credit Card</option>
                  <option value="DEBIT_CARD">Debit Card</option>
                  <option value="PAYPAL">PayPal</option>
                  <option value="BANK_TRANSFER">Bank Transfer</option>
                  <option value="UPI">UPI</option>
                </select>
              </div>

              <div className="payment-summary">
                <div className="summary-row">
                  <span>Payment Amount: </span>
                  <strong>${newPayment.amount.toFixed(2)}</strong>
                </div>
                <div className="summary-row">
                  <span>Payment Method: </span>
                  <strong>{newPayment.paymentMethod?.replace('_', ' ')}</strong>
                </div>
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  onClick={() => setActiveTab('list')}
                  className="cancel-btn"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={creating}
                  className="submit-btn"
                >
                  {creating ? 'Creating...' : 'Create Payment'}
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {activeTab === 'history' && (
        <div className="payment-history">
          {/* Add search to history tab as well */}
          <div className="orders-header-section">
            <div className="orders-title-section">
              <h3>Payment History ({payments.length})</h3>
            </div>
            
            <div className="search-container-centered">
              <input
                type="text"
                placeholder="Search payment history..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input-centered"
              />
            </div>
            
            <div className="pagination-info-section">
              <button onClick={loadPaymentData} className="refresh-btn">Refresh</button>
            </div>
          </div>
          
          <div className="history-summary">
            <div className="summary-cards">
              <div className="summary-card">
                <h4>This Month</h4>
                <div className="summary-amount">
                  ${payments
                    .filter(p => {
                      if (!p.createdAt) return false;
                      const paymentDate = new Date(p.createdAt[0], p.createdAt[1] - 1, p.createdAt[2]);
                      const now = new Date();
                      return paymentDate.getMonth() === now.getMonth() && paymentDate.getFullYear() === now.getFullYear();
                    })
                    .reduce((sum, p) => sum + (p.amount || 0), 0)
                    .toFixed(2)}
                </div>
              </div>
              <div className="summary-card">
                <h4>Last 30 Days</h4>
                <div className="summary-amount">
                  ${payments
                    .filter(p => {
                      if (!p.createdAt) return false;
                      const paymentDate = new Date(p.createdAt[0], p.createdAt[1] - 1, p.createdAt[2]);
                      const thirtyDaysAgo = new Date();
                      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                      return paymentDate >= thirtyDaysAgo;
                    })
                    .reduce((sum, p) => sum + (p.amount || 0), 0)
                    .toFixed(2)}
                </div>
              </div>
              <div className="summary-card">
                <h4>All Time</h4>
                <div className="summary-amount">
                  ${stats.totalAmount.toFixed(2)}
                </div>
              </div>
            </div>
          </div>

          <div className="orders-table-modern">
            <table>
              <thead>
                <tr>
                  <th>Payment ID</th>
                  <th>Order ID</th>
                  <th>Amount</th>
                  <th>Method</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {getFilteredAndSearchedPayments()
                  .sort((a, b) => {
                    if (!a.createdAt || !b.createdAt) return 0;
                    const dateA = new Date(a.createdAt[0], a.createdAt[1] - 1, a.createdAt[2], a.createdAt[3], a.createdAt[4], a.createdAt[5]);
                    const dateB = new Date(b.createdAt[0], b.createdAt[1] - 1, b.createdAt[2], b.createdAt[3], b.createdAt[4], b.createdAt[5]);
                    return dateB - dateA;
                  })
                  .map(payment => (
                    <tr key={payment.id}>
                      <td className="order-id-cell">#{payment.id}</td>
                      <td className="order-id-cell">#{payment.orderId}</td>
                      <td className="amount-cell-modern">${payment.amount?.toFixed(2)}</td>
                      <td className="category-cell">
                        <span className="method-badge">
                          {getMethodIcon(payment.paymentMethod)} {getMethodDisplay(payment.paymentMethod)}
                        </span>
                      </td>
                      <td>
                        <span 
                          className="status-badge-modern"
                          style={{ 
                            color: getStatusColor(payment.status), 
                            backgroundColor: "white", 
                            borderColor: getStatusColor(payment.status), 
                            border: "2px solid"
                          }}
                        >
                          {payment.status || 'PENDING'}
                        </span>
                      </td>
                      <td className="date-cell">{formatDate(payment.createdAt)}</td>
                      <td>
                        <button 
                          className="action-btn-modern"
                          onClick={() => viewPaymentDetails(payment.id)}
                          title="View Payment Details"
                        >
                          V
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Payment Details Modal */}
      {selectedPayment && (
        <div className="modal-overlay" onClick={() => setSelectedPayment(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Payment Details #{selectedPayment.id}</h3>
              <button 
                onClick={() => setSelectedPayment(null)}
                className="modal-close"
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="details-section">
                <h4>Payment Information</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Payment ID:</label>
                    <span>{selectedPayment.id}</span>
                  </div>
                  <div className="detail-item">
                    <label>Order ID:</label>
                    <span>#{selectedPayment.orderId}</span>
                  </div>
                  <div className="detail-item">
                    <label>Amount:</label>
                    <span className="amount">${selectedPayment.amount?.toFixed(2)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Payment Method:</label>
                    <span>{selectedPayment.paymentMethod?.replace('_', ' ')}</span>
                  </div>
                  <div className="detail-item">
                    <label>Status:</label>
                    <span 
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(selectedPayment.status) }}
                    >
                      {selectedPayment.status || 'PENDING'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Customer ID:</label>
                    <span>{selectedPayment.customerId || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Created:</label>
                    <span>{formatDate(selectedPayment.createdAt)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Updated:</label>
                    <span>{formatDate(selectedPayment.updatedAt)}</span>
                  </div>
                  {selectedPayment.processedAt && (
                    <div className="detail-item">
                      <label>Processed:</label>
                      <span>{formatDate(selectedPayment.processedAt)}</span>
                    </div>
                  )}
                  {selectedPayment.transactionId && (
                    <div className="detail-item">
                      <label>Transaction ID:</label>
                      <span className="transaction-id">{selectedPayment.transactionId}</span>
                    </div>
                  )}
                  {selectedPayment.gatewayResponse && (
                    <div className="detail-item">
                      <label>Gateway Response:</label>
                      <span>{selectedPayment.gatewayResponse}</span>
                    </div>
                  )}
                  {selectedPayment.failureReason && (
                    <div className="detail-item">
                      <label>Failure Reason:</label>
                      <span className="failure-reason">{selectedPayment.failureReason}</span>
                    </div>
                  )}
                  <div className="detail-item">
                    <label>Retry Count:</label>
                    <span>{selectedPayment.retryCount || 0}</span>
                  </div>
                </div>
              </div>

              <div className="modal-actions">
                {selectedPayment.status?.toLowerCase() === 'pending' && (
                  <button 
                    onClick={() => {
                      processPayment(selectedPayment.id);
                      setSelectedPayment(null);
                    }}
                    className="process-payment-btn"
                  >
                    Process Payment
                  </button>
                )}
                <button 
                  onClick={() => setSelectedPayment(null)}
                  className="close-modal-btn"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payments;